import { useWorkspaceText } from '../interface-copy';
import { LockKeyhole, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { NativePostgresConfig } from './model';

export function CredentialsForm({
  config,
  advanced,
  existing,
  busy,
  loadingExisting,
  onChange,
  onAdvanced,
  onTest,
  onRemove,
  valid,
}: {
  config: NativePostgresConfig;
  advanced: boolean;
  existing: boolean;
  busy: boolean;
  loadingExisting: boolean;
  onChange: (next: Partial<NativePostgresConfig>) => void;
  onAdvanced: () => void;
  onTest: () => void;
  onRemove: () => void;
  valid: boolean;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  return (
    <div className="ws-pg-form">
      <fieldset disabled={busy || loadingExisting}>
        <label>
          {uiRich('Name {value1}', {
            value1: (
              <input
                value={config.name}
                onChange={(event) => onChange({ name: event.currentTarget.value })}
                autoFocus
              />
            ),
          })}
        </label>
        <label>
          {uiRich('Host {value1}', {
            value1: (
              <input
                value={config.host}
                onChange={(event) => onChange({ host: event.currentTarget.value })}
                placeholder={uiText('db.example.com')}
              />
            ),
          })}
        </label>
        <div className="ws-pg-inline">
          <label>
            {uiRich('Port {value1}', {
              value1: (
                <input
                  inputMode="numeric"
                  value={config.port}
                  onChange={(event) => onChange({ port: event.currentTarget.value })}
                />
              ),
            })}
          </label>
          <label>
            {uiRich('Database {value1}', {
              value1: (
                <input
                  value={config.database}
                  onChange={(event) => onChange({ database: event.currentTarget.value })}
                />
              ),
            })}
          </label>
        </div>
        <label>
          {uiRich('Username {value1}', {
            value1: (
              <input
                value={config.username}
                onChange={(event) => onChange({ username: event.currentTarget.value })}
              />
            ),
          })}
        </label>
        <label>
          {uiRich('Password {value1}', {
            value1: (
              <input
                type="password"
                autoComplete="new-password"
                value={config.password}
                onChange={(event) => onChange({ password: event.currentTarget.value })}
              />
            ),
          })}
        </label>
        <button
          type="button"
          className="ws-pg-advanced"
          onClick={onAdvanced}
          aria-expanded={advanced}
        >
          {uiText('Advanced TLS')}
        </button>
        {advanced && (
          <div className="ws-pg-tls">
            <p>
              {uiRich(
                '{value1} Poneva verifies the server certificate. Add a CA or server DNS name for IP hosts when your certificate requires it.',
                { value1: <LockKeyhole size={14} /> },
              )}
            </p>
            <label>
              {uiRich('CA certificate {value1}', {
                value1: (
                  <textarea
                    value={config.ca}
                    onChange={(event) => onChange({ ca: event.currentTarget.value })}
                  />
                ),
              })}
            </label>
            <label>
              {uiRich('Certificate server DNS name {value1}', {
                value1: (
                  <input
                    value={config.servername}
                    onChange={(event) => onChange({ servername: event.currentTarget.value })}
                  />
                ),
              })}
            </label>
          </div>
        )}
        <p className="ws-pg-note">
          {uiRich(
            '{value1} Your password is encrypted in secure storage. Poneva verifies the connection before saving it.',
            { value1: <LockKeyhole size={14} /> },
          )}
        </p>
      </fieldset>
      <footer className="ws-pg-actions">
        {existing && (
          <Button variant="outline" onClick={onRemove} disabled={busy || loadingExisting}>
            {uiText('Disconnect')}
          </Button>
        )}
        <Button onClick={onTest} disabled={!valid || busy || loadingExisting}>
          {busy ? uiText('Testing…') : uiText('Test connection')}
        </Button>
      </footer>
    </div>
  );
}

export function SuccessState({
  enabled,
  writeAllowed,
  grantError,
  busy,
  onRetry,
  onRemove,
  onTryInChat,
  onDone,
}: {
  enabled: boolean;
  writeAllowed: boolean;
  grantError: string;
  busy: boolean;
  onRetry: () => void;
  onRemove: () => void;
  onTryInChat?: (prompt: string) => void;
  onDone: () => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  return (
    <div className="ws-pg-success">
      <ShieldCheck size={24} />
      <p>
        <strong>
          {enabled ? uiText('PostgreSQL is connected.') : uiText('PostgreSQL was saved safely.')}
        </strong>
        <br />
        {enabled
          ? writeAllowed
            ? uiText(
                'Poneva can read and change rows in the selected tables. Apps also follow their approved data scope.',
              )
            : uiText(
                'Poneva can read the selected tables. Apps also follow their approved data scope.',
              )
          : uiText('Reads are paused until permission is enabled.')}
      </p>
      {grantError && (
        <p className="ws-pg-error" role="alert">
          {grantError}
        </p>
      )}
      {!enabled && (
        <Button onClick={onRetry} disabled={busy}>
          {busy ? uiText('Retrying…') : uiText('Retry permission')}
        </Button>
      )}
      {enabled && onTryInChat && (
        <Button
          variant="outline"
          onClick={() => onTryInChat('Use the connected PostgreSQL source for this task.')}
        >
          {uiText('Try it in Chat')}
        </Button>
      )}
      <Button onClick={onDone} disabled={busy}>
        {uiText('Done')}
      </Button>
      <Button variant="ghost" onClick={onRemove} disabled={busy}>
        {uiText('Disconnect database')}
      </Button>
    </div>
  );
}
